import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, finalize, Observable, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  DeleteRouteResponse,
  GetAllRoutes,
  RouteAdmin,
} from 'src/app/core/models/admin';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class RouteService {
  baseUrl = environment.apiUrl.concat('route-management/');

  constructor(
    private http: HttpClient,
    private commonUtils: CommonUtils,
    private errorHandler: ErrorhandleService,
  ) {}

  getAllRoutes(
    location: string,
    errorBanner = BANNER_TYPE.TABLE_DATA_ERROR,
  ): Observable<GetAllRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetAllRoutes>(this.baseUrl.concat('get-all-routes'), {
        params: { locationCode: location },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetAllRoutes>(
            error,
            error.status,
            'Route Service: Get All Routes',
            errorBanner,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  editRoute(route: RouteAdmin): Observable<GetAllRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post<GetAllRoutes>(
        this.baseUrl.concat('edit-Route'),
        { route: route },
        { params: { routeid: route.id } },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetAllRoutes>(
            error,
            error.status,
            'Route Service: Edit Route',
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  createRoute(
    newRoute: RouteAdmin,
    location: string,
  ): Observable<GetAllRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post<GetAllRoutes>(
        this.baseUrl.concat('Add-Route'),
        { route: newRoute },
        { params: { locationCode: location } },
      )
      .pipe(
        take(1),
        catchError((error) => {
          //Handle a specific error if route id already exists
          if (error.error.code === 400) {
            this.commonUtils.updateBannerType(BANNER_TYPE.ROUTE_EXISTS);
            return of(false as unknown as GetAllRoutes);
          } else {
            return this.errorHandler.handleError<GetAllRoutes>(
              error,
              error.status,
              'Route Service: Create Route',
            );
          }
        }),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  deleteRoute(
    routeId: string,
    locationCode: string,
    deletedById: string,
  ): Observable<DeleteRouteResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .delete<DeleteRouteResponse>(this.baseUrl.concat('Delete-Route'), {
        headers: { loginId: deletedById },
        params: { routeid: routeId, locationcode: locationCode },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<DeleteRouteResponse>(
            error,
            error.status,
            'Route Service: Delete Route',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
